/**
 * Fair Leaderboard Scoring System
 * 
 * This implements a comprehensive scoring algorithm that accounts for:
 * 1. Wilson Score - Statistical confidence based on sample size
 * 2. Proximity Score - Partial credit for "close misses"
 * 3. Directional Accuracy - Bonus for correct market direction
 * 4. Volume Factor - Rewards consistent participation
 * 5. Lead Time Bonus - Harder predictions (longer timeframes) get more credit
 */

import { Prediction } from '../types/predictions';

interface PriceDataPoint {
  time: string;
  value: number;
}

interface ScoringResult {
  name: string;
  profileImage?: string;
  
  // Raw counts
  successes: number;
  misses: number;
  pending: number;
  total: number;
  
  // Traditional rate (for comparison)
  traditionalRate: number;
  
  // Fair scoring components
  proximityCredits: number;      // Partial credit from close misses
  directionalBonuses: number;    // Bonus points from correct direction
  
  // Final scores
  fairScore: number;             // Composite fair score (0-100)
  wilsonScore: number;           // Statistical confidence
  volumeFactor: number;          // Sample size multiplier
  
  // Display
  displayScore: number;          // The main score to show
  scoringDetails?: string;       // Explanation for tooltip
}

/**
 * Wilson Score Lower Bound
 * Used by Reddit, Yelp, Amazon for ranking with confidence intervals
 * Handles small sample sizes fairly
 */
function wilsonScoreLowerBound(successes: number, total: number, z: number = 1.96): number {
  if (total === 0) return 0;
  
  const p = successes / total;
  const denominator = 1 + z * z / total;
  const centre = p + z * z / (2 * total);
  const deviation = z * Math.sqrt((p * (1 - p) + z * z / (4 * total)) / total);
  
  return Math.max(0, (centre - deviation) / denominator);
}

/**
 * Get the actual price at a specific date from chart data
 */
function getPriceAtDate(chartData: PriceDataPoint[], targetDate: Date): number | null {
  if (!chartData || chartData.length === 0) return null;
  
  const targetTime = targetDate.getTime();
  
  // Find closest price point
  let closest = chartData[0];
  let minDiff = Math.abs(new Date(closest.time).getTime() - targetTime);
  
  for (const point of chartData) {
    const diff = Math.abs(new Date(point.time).getTime() - targetTime);
    if (diff < minDiff) {
      minDiff = diff;
      closest = point;
    }
  }
  
  // Only use if within 3 days of target
  if (minDiff > 3 * 24 * 60 * 60 * 1000) return null;
  
  return closest.value;
}

/**
 * Calculate proximity score for a miss
 * Returns 0-1 based on how close the prediction was
 */
function calculateProximityScore(
  predictedPrice: number,
  actualPrice: number
): number {
  if (!predictedPrice || !actualPrice) return 0;
  
  const percentError = Math.abs(predictedPrice - actualPrice) / actualPrice * 100;
  
  // Sliding scale of partial credit
  if (percentError <= 5) return 0.90;   // Within 5%: 90% credit
  if (percentError <= 10) return 0.75;  // Within 10%: 75% credit
  if (percentError <= 15) return 0.60;  // Within 15%: 60% credit
  if (percentError <= 20) return 0.45;  // Within 20%: 45% credit
  if (percentError <= 30) return 0.30;  // Within 30%: 30% credit
  if (percentError <= 50) return 0.15;  // Within 50%: 15% credit
  return 0.05;                           // Over 50%: minimal credit for trying
}

/**
 * Determine if prediction got the direction right
 * Returns true if predicted direction matches actual movement
 */
function wasDirectionallyCorrect(
  prediction: Prediction,
  priceAtPrediction: number | null,
  priceAtTarget: number | null
): boolean {
  if (!priceAtPrediction || !priceAtTarget) return false;
  
  const actualDirection = priceAtTarget > priceAtPrediction ? 'up' : 'down';
  
  // Get predicted target price
  const targetPrice = Number(prediction.price_prediction) || 
                      prediction.price_target || 
                      prediction.price_min || 0;
  
  if (!targetPrice) return false;
  
  const predictedDirection = targetPrice > priceAtPrediction ? 'up' : 'down';
  
  return actualDirection === predictedDirection;
}

/**
 * Calculate volume factor - rewards consistent participation
 * sqrt(n/10) up to 1.0, so 10+ predictions = full credit
 */
function calculateVolumeFactor(totalPredictions: number): number {
  return Math.min(1.0, Math.sqrt(totalPredictions / 10));
}

/**
 * Calculate lead time bonus based on prediction timeframe
 * Longer predictions are harder and deserve more credit
 */
function calculateLeadTimeBonus(predictionMade: Date | null, targetDate: Date | null): number {
  if (!predictionMade || !targetDate) return 1.0;
  
  const daysDiff = (targetDate.getTime() - predictionMade.getTime()) / (1000 * 60 * 60 * 24);
  
  if (daysDiff >= 365) return 1.3;   // 1+ year ahead: 30% bonus
  if (daysDiff >= 180) return 1.2;   // 6+ months: 20% bonus
  if (daysDiff >= 90) return 1.1;    // 3+ months: 10% bonus
  if (daysDiff >= 30) return 1.0;    // 1+ month: standard
  return 0.9;                         // Under 1 month: 10% reduction
}

/**
 * Main scoring function - calculates fair leaderboard scores
 */
export function calculateFairLeaderboard(
  predictions: Prediction[],
  chartData: PriceDataPoint[],
  maxEntries: number = 10
): ScoringResult[] {
  const influencerMap = new Map<string, {
    name: string;
    profileImage?: string;
    predictions: Prediction[];
    successes: number;
    misses: number;
    pending: number;
  }>();
  
  // Group predictions by influencer
  predictions.forEach(pred => {
    // Skip sentiment predictions - they don't have success/miss outcomes
    if (pred.prediction_type === 'sentiment') return;
    
    const name = pred.influencer?.name || pred.expert_name || 'Unknown';
    const profileImage = pred.influencer?.profile_image;
    
    if (!influencerMap.has(name)) {
      influencerMap.set(name, {
        name,
        profileImage,
        predictions: [],
        successes: 0,
        misses: 0,
        pending: 0
      });
    }
    
    const entry = influencerMap.get(name)!;
    entry.predictions.push(pred);
    
    if (pred.success_status === 'success') {
      entry.successes++;
    } else if (pred.success_status === 'miss') {
      entry.misses++;
    } else {
      entry.pending++;
    }
    
    // Update profile image if we find one
    if (profileImage && !entry.profileImage) {
      entry.profileImage = profileImage;
    }
  });
  
  // Calculate scores for each influencer
  const results: ScoringResult[] = [];
  
  influencerMap.forEach((data, name) => {
    const resolvedCount = data.successes + data.misses;
    
    // Skip if no resolved predictions
    if (resolvedCount < 1) return;
    
    let proximityCredits = 0;
    let directionalBonuses = 0;
    let totalLeadTimeBonus = 0;
    let leadTimePredictions = 0;
    
    // Calculate partial credits for each prediction
    data.predictions.forEach(pred => {
      if (pred.success_status !== 'miss') return; // Only process misses for partial credit
      
      // Get target price
      const targetPrice = Number(pred.price_prediction) || 
                         pred.price_target || 
                         pred.price_min || 0;
      
      if (!targetPrice) return;
      
      // Get target date
      const targetDate = pred.end_date || pred.target_time;
      if (!targetDate) return;
      
      const targetDateObj = new Date(targetDate);
      
      // Get actual price at target date
      const actualPrice = getPriceAtDate(chartData, targetDateObj);
      if (!actualPrice) return;
      
      // Calculate proximity credit
      const proximity = calculateProximityScore(targetPrice, actualPrice);
      proximityCredits += proximity;
      
      // Check directional accuracy
      const predictionMadeDate = pred.prediction_made ? new Date(pred.prediction_made) : 
                                 pred.created_at ? new Date(pred.created_at) : null;
      
      if (predictionMadeDate) {
        const priceAtPrediction = getPriceAtDate(chartData, predictionMadeDate);
        
        if (wasDirectionallyCorrect(pred, priceAtPrediction, actualPrice)) {
          directionalBonuses += 0.25; // 25% bonus for correct direction
        }
        
        // Lead time bonus
        const leadBonus = calculateLeadTimeBonus(predictionMadeDate, targetDateObj);
        totalLeadTimeBonus += leadBonus;
        leadTimePredictions++;
      }
    });
    
    // Also add lead time for successes
    data.predictions.forEach(pred => {
      if (pred.success_status !== 'success') return;
      
      const targetDate = pred.end_date || pred.target_time;
      const predictionMadeDate = pred.prediction_made ? new Date(pred.prediction_made) : 
                                 pred.created_at ? new Date(pred.created_at) : null;
      
      if (predictionMadeDate && targetDate) {
        const leadBonus = calculateLeadTimeBonus(predictionMadeDate, new Date(targetDate));
        totalLeadTimeBonus += leadBonus;
        leadTimePredictions++;
      }
    });
    
    // Calculate average lead time multiplier
    const avgLeadTimeBonus = leadTimePredictions > 0 
      ? totalLeadTimeBonus / leadTimePredictions 
      : 1.0;
    
    // Traditional success rate
    const traditionalRate = Math.round((data.successes / resolvedCount) * 100);
    
    // Fair score calculation
    // Full credit for successes + partial credit for misses + directional bonuses
    const rawScore = (data.successes * 1.0 + proximityCredits + directionalBonuses) / resolvedCount;
    
    // Apply Wilson confidence (penalizes small samples)
    const wilsonScore = wilsonScoreLowerBound(data.successes + proximityCredits, resolvedCount);
    
    // Volume factor (rewards consistent predictors)
    const volumeFactor = calculateVolumeFactor(resolvedCount);
    
    // Final composite score
    const fairScore = Math.round(
      rawScore * 100 * 
      Math.sqrt(wilsonScore) *  // Square root to soften Wilson's impact
      Math.pow(volumeFactor, 0.5) * // Square root of volume factor
      avgLeadTimeBonus
    );
    
    results.push({
      name,
      profileImage: data.profileImage,
      successes: data.successes,
      misses: data.misses,
      pending: data.pending,
      total: data.predictions.length,
      traditionalRate,
      proximityCredits: Math.round(proximityCredits * 100) / 100,
      directionalBonuses: Math.round(directionalBonuses * 100) / 100,
      fairScore: Math.min(100, fairScore), // Cap at 100
      wilsonScore: Math.round(wilsonScore * 100) / 100,
      volumeFactor: Math.round(volumeFactor * 100) / 100,
      displayScore: Math.min(100, fairScore),
      scoringDetails: `Proximity bonus: +${(proximityCredits * 100).toFixed(0)}% | Direction: +${(directionalBonuses * 100).toFixed(0)}%`
    });
  });
  
  // Sort by fair score, then by successes
  return results
    .sort((a, b) => {
      if (b.displayScore !== a.displayScore) {
        return b.displayScore - a.displayScore;
      }
      return b.successes - a.successes;
    })
    .slice(0, maxEntries);
}

export type { ScoringResult, PriceDataPoint };
